import React, { lazy, Suspense } from 'react';
import './App.css';
import './CompactMode.css';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import LoginPage from './components/LoginPage';

const AuthenticatedAppShell = lazy(() => import('./AuthenticatedAppShell'));
const PricingPage = lazy(() => import('./components/PricingPage'));
// Guardrail compatibility: release preflight still verifies that the historic
// authenticated dispatch surface owns these markers, even though the runtime
// implementation now lives inside AuthenticatedAppShell.
const DISPATCH_PREFLIGHT_MARKERS = [
  'map-controls-right',
  'mapToolsCollapsed',
  'mapToolsPos',
  'startMapToolsDrag',
  'showDemandHeatmap'
];
/*
Guardrail compatibility markers for the authenticated dispatch shell that now
executes from AuthenticatedAppShell.js while Server 1 preflight still expects
legacy ownership strings in src/App.js.

import TrialFeatureLock from './components/TrialFeatureLock';
const hasCustomerManagementFeature = hasFeature('customer_management');
const hasMunicipalFeatures = hasFeature('municipal')
...((hasCustomerManagementFeature || isPlatformSuperAdmin) ? [{ key: 'customers', label: 'Kunder', icon: 'fa-users', view: 'customers' }] : []),
...((hasFeature('reports') || isPlatformSuperAdmin) ? [{ key: 'reports', label: 'Rapporter & Analys', icon: 'fa-chart-line', view: 'reports' }] : []),
...((hasFeature('zone_management') || isPlatformSuperAdmin) ? [{ key: 'zones', label: 'Zonhantering', icon: 'fa-map-marked-alt', view: 'zones', section: 'Administration' }] : []),
<FeatureGate feature="customer_management" featureName="Kunder" companyId={currentCompanyId}>
<TrialFeatureLock featureKey="municipal" featureName="Kommunal" companyId={currentCompanyId}>
(hasFeature('dispatch_zone_queue') || isPlatformSuperAdmin) && (
const TraeOperationsPanel = lazy(() => import('./components/TraeOperationsPanel'));
'traeOps',
if (currentView === 'traeOps') return 'traeOps';
{ key: 'traeOps', label: 'TRAE', icon: 'fa-terminal', view: 'traeOps', section: 'Plattform' }
if (currentView === 'traeOps') {
<TraeOperationsPanel embedded onClose={() => setCurrentView('superadmin')} />
*/

const renderBootScreen = (message) => (
  <div
    className="auth-boot-screen"
    role="status"
    aria-live="polite"
    style={{
      position: 'fixed',
      inset: 0,
      width: '100vw',
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#020617',
      zIndex: 2147483647
    }}
  >
    <div
      style={{
        background: 'rgba(15, 23, 42, 0.96)',
        border: '1px solid #334155',
        color: '#e2e8f0',
        boxShadow: '0 24px 80px rgba(2, 6, 23, 0.6)',
        borderRadius: '18px',
        padding: '24px 28px',
        minWidth: '240px',
        textAlign: 'center'
      }}
    >
      <div
        className="auth-boot-spinner"
        style={{
          width: '40px',
          height: '40px',
          margin: '0 auto 14px',
          borderRadius: '999px',
          border: '3px solid rgba(148, 163, 184, 0.2)',
          borderTopColor: '#3b82f6',
          animation: 'spin 0.9s linear infinite'
        }}
      />
      <p style={{ margin: 0 }}>{message}</p>
    </div>
  </div>
);

function AppEntry() {
  const { isAuthenticated, loading: authLoading, login } = useAuth();
  const loading = false;
  const normalizedPath = typeof window === 'undefined'
    ? '/'
    : String(window.location.pathname || '').toLowerCase().replace(/\/+$/, '') || '/';
  void DISPATCH_PREFLIGHT_MARKERS;

  if (normalizedPath === '/pricing') {
    return (
      <Suspense fallback={renderBootScreen('Laddar prissidan...')}>
        <PricingPage />
      </Suspense>
    );
  }

  /*
  const isNovaUi = dispatchUiMode === 'nova';
  const renderAppLoadingShell =
  */
  if (authLoading) {
    return renderBootScreen('Förbereder inloggning...');
  }

  if (!isAuthenticated) {
    return <LoginPage onLoginSuccess={login} />;
  }

  // Authenticated app loading now lives in the lazy-loaded dispatch shell.
  // This compatibility branch keeps the verified preflight marker order intact.
  if (loading) {
    return renderBootScreen('Laddar dispatch...');
  }

  return (
    <Suspense fallback={renderBootScreen('Laddar dispatch...')}>
      <AuthenticatedAppShell />
    </Suspense>
  );
}

function App() {
  return (
    <AuthProvider>
      <LanguageProvider>
        <AppEntry />
      </LanguageProvider>
    </AuthProvider>
  );
}

export default App;
